// -------------------------------------------------------------------------
// PDF + CSV report generation for the admin Result Detail page.
// Uses stored `accuracy` / `wpm` values as-is — nothing recalculated.
// -------------------------------------------------------------------------

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { lcsDiff, categorizeMistakes, summarize, CATEGORY_LABEL, explain } from "./reportDiff";

const BRAND = { name: "DataSprint", primary: [225, 29, 72] }; // rose-600

function header(doc, title) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageW, 18, "F");
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(BRAND.name, 12, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(title, pageW - 12, 12, { align: "right" });
  doc.setTextColor(0);
}

function footer(doc) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Generated by ${BRAND.name} • ${new Date().toLocaleString()}`, 12, pageH - 6);
    doc.text(`Page ${i} of ${total}`, pageW - 12, pageH - 6, { align: "right" });
  }
}

function meta(doc, rows, startY) {
  autoTable(doc, {
    startY,
    body: rows,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 1.6 },
    columnStyles: {
      0: { fontStyle: "bold", textColor: [90, 90, 90], cellWidth: 45 },
      1: { textColor: [20, 20, 20] },
    },
  });
  return doc.lastAutoTable.finalY;
}

function fieldBlock(doc, field, pageName, startY) {
  const pageW = doc.internal.pageSize.getWidth();
  const usable = pageW - 24;
  const original = field.original || "";
  const typed = field.typed || "";
  const ops = lcsDiff(original, typed);
  const mistakes = categorizeMistakes(ops);
  const s = summarize(mistakes);

  // Section title
  if (startY > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); startY = 22; }
  doc.setFillColor(245, 245, 245);
  doc.rect(12, startY, usable, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20);
  doc.text(`${pageName} — Field ${field.index + 1}`, 15, startY + 5.5);
  doc.setFont("helvetica", "normal");
  doc.text(`Existing Accuracy: ${field.accuracy}%`, pageW - 15, startY + 5.5, { align: "right" });
  let y = startY + 12;

  // Original / Typed side-by-side
  autoTable(doc, {
    startY: y,
    head: [["Original Text", "Candidate Typed"]],
    body: [[original || "—", typed || "—"]],
    theme: "grid",
    styles: { font: "courier", fontSize: 9, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [10, 10, 10], textColor: 255, fontSize: 8, font: "helvetica" },
    columnStyles: { 0: { cellWidth: usable / 2 }, 1: { cellWidth: usable / 2 } },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Mistake summary chip row
  autoTable(doc, {
    startY: y,
    head: [["Total", "Missing", "Extra", "Wrong / Replaced", "Missing Spaces", "Extra Spaces", "Case", "Symbol"]],
    body: [[s.total, s.missing, s.extra, s.wrong, s.missing_space, s.extra_space, s.case, s.symbol]],
    theme: "grid",
    styles: { fontSize: 8.5, halign: "center", cellPadding: 2 },
    headStyles: { fillColor: [30, 30, 30], textColor: 255 },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Detailed mistakes table
  if (mistakes.length) {
    autoTable(doc, {
      startY: y,
      head: [["#", "Type", "Position", "Expected", "Typed", "Explanation"]],
      body: mistakes.slice(0, 200).map((m, i) => [
        i + 1,
        CATEGORY_LABEL[m.category] || m.category,
        m.position,
        m.expected === null || m.expected === undefined ? "—" : m.expected === " " ? "[SPACE]" : m.expected,
        m.typed === null || m.typed === undefined ? "—" : m.typed === " " ? "[SPACE]" : m.typed,
        explain(m),
      ]),
      theme: "striped",
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [225, 29, 72], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 26 },
        2: { cellWidth: 16, halign: "center" },
        3: { cellWidth: 22, font: "courier" },
        4: { cellWidth: 22, font: "courier" },
        5: { cellWidth: "auto" },
      },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY;
    if (mistakes.length > 200) {
      doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`(showing first 200 of ${mistakes.length} mistakes)`, 12, y + 5);
      doc.setTextColor(0);
      y += 5;
    }
  } else {
    autoTable(doc, {
      startY: y,
      body: [["No mistakes detected in this field."]],
      theme: "plain",
      styles: { fontSize: 9, textColor: [22, 163, 74], fontStyle: "italic" },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY;
  }
  return y + 4;
}

function metaRowsForSubmission(sub, employee, pageName) {
  return [
    ["Candidate", `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() || "—"],
    ["Email", employee?.email || "—"],
    ["Page", pageName || "—"],
    ["Submitted", sub?.submitted_at ? new Date(sub.submitted_at).toLocaleString() : "—"],
    ["Overall Accuracy", `${sub?.accuracy ?? "—"}%`],
    ["WPM", sub?.wpm ?? "—"],
    ["Mistakes (stored)", sub?.mistakes ?? "—"],
    ["Time Taken", sub?.time_taken_seconds ? `${Math.round(sub.time_taken_seconds)}s` : "—"],
  ];
}

// --- PUBLIC: single-page PDF (from AdminSubmissionDetail) --------------
export function downloadPagePdf({ pageName, fields, submission, employee }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, `Result Report — ${pageName || "Page"}`);
  let y = 24;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text(`${pageName || "Page"} — Result Detail`, 12, y);
  y += 6;
  y = meta(doc, metaRowsForSubmission(submission, employee, pageName), y);
  y += 4;
  for (const f of fields) y = fieldBlock(doc, f, pageName, y);
  footer(doc);
  doc.save(`DataSprint_Report_${(pageName || "Page").replace(/\s+/g, "_")}.pdf`);
}

// --- PUBLIC: single-page CSV -----------------------------------------
export function downloadPageCsv({ pageName, fields, submission, employee }) {
  const rows = [
    ["DataSprint Result Report"],
    ["Candidate", `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim()],
    ["Email", employee?.email || ""],
    ["Page", pageName || ""],
    ["Submitted", submission?.submitted_at || ""],
    ["Overall Accuracy (%)", submission?.accuracy ?? ""],
    ["WPM", submission?.wpm ?? ""],
    ["Mistakes (stored)", submission?.mistakes ?? ""],
    [],
    ["Field", "Existing Accuracy (%)", "Original", "Typed", "Mistake #", "Type", "Position", "Expected", "Typed Char", "Explanation"],
  ];
  for (const f of fields) {
    const ops = lcsDiff(f.original || "", f.typed || "");
    const mistakes = categorizeMistakes(ops);
    if (!mistakes.length) {
      rows.push([`Field ${f.index + 1}`, f.accuracy, f.original || "", f.typed || "", "", "None", "", "", "", "No mistakes"]);
      continue;
    }
    mistakes.forEach((m, i) => {
      rows.push([
        i === 0 ? `Field ${f.index + 1}` : "",
        i === 0 ? f.accuracy : "",
        i === 0 ? (f.original || "") : "",
        i === 0 ? (f.typed || "") : "",
        i + 1,
        CATEGORY_LABEL[m.category] || m.category,
        m.position,
        m.expected === null ? "" : m.expected === " " ? "[SPACE]" : m.expected,
        m.typed === null ? "" : m.typed === " " ? "[SPACE]" : m.typed,
        explain(m),
      ]);
    });
  }
  saveCsv(rows, `DataSprint_Report_${(pageName || "Page").replace(/\s+/g, "_")}.csv`);
}

// --- PUBLIC: full multi-page PDF (from AdminEmployeeResult) ----------
// pages = [{ pageName, fields, submission }]
export function downloadFullPdf({ employee, aggregate, pages }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, `Full Result — ${(employee?.first_name || "") + " " + (employee?.last_name || "")}`);
  let y = 24;
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text("Full Candidate Result Report", 12, y);
  y += 8;

  y = meta(doc, [
    ["Candidate", `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim()],
    ["Email", employee?.email || "—"],
    ["Assigned Range", `${employee?.from_page || "—"} – ${employee?.to_page || "—"}`],
    ["Total Pages Assigned", employee?.total_pages ?? "—"],
    ["Completed Pages", employee?.completed_pages ?? "—"],
    ["Overall Real Accuracy", `${aggregate?.total_accuracy ?? "—"}%`],
    ["Overall Display Accuracy", `${aggregate?.display_accuracy ?? "—"}%`],
    ["Average WPM", aggregate?.avg_wpm ?? "—"],
    ["Total Mistakes (stored)", aggregate?.total_mistakes ?? "—"],
  ], y);
  y += 4;

  for (const p of pages) {
    if (y > doc.internal.pageSize.getHeight() - 60) { doc.addPage(); y = 22; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(225, 29, 72);
    doc.text(`${p.pageName} — Accuracy ${p.submission?.accuracy ?? "—"}% · WPM ${p.submission?.wpm ?? "—"}`, 12, y);
    doc.setTextColor(0);
    y += 4;
    for (const f of p.fields) y = fieldBlock(doc, f, p.pageName, y);
    y += 2;
  }
  footer(doc);
  const nm = `${(employee?.first_name || "Candidate")}_${employee?.last_name || ""}`.trim().replace(/\s+/g, "_");
  doc.save(`DataSprint_FullReport_${nm}.pdf`);
}

// --- PUBLIC: full multi-page CSV -------------------------------------
export function downloadFullCsv({ employee, aggregate, pages }) {
  const rows = [
    ["DataSprint Full Result Report"],
    ["Candidate", `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim()],
    ["Email", employee?.email || ""],
    ["Assigned Range", `${employee?.from_page || ""} – ${employee?.to_page || ""}`],
    ["Overall Real Accuracy (%)", aggregate?.total_accuracy ?? ""],
    ["Overall Display Accuracy (%)", aggregate?.display_accuracy ?? ""],
    ["Average WPM", aggregate?.avg_wpm ?? ""],
    ["Total Mistakes (stored)", aggregate?.total_mistakes ?? ""],
    [],
    ["Page", "Field", "Existing Accuracy (%)", "Original", "Typed", "Mistake #", "Type", "Position", "Expected", "Typed Char", "Explanation"],
  ];
  for (const p of pages) {
    for (const f of p.fields) {
      const ops = lcsDiff(f.original || "", f.typed || "");
      const mistakes = categorizeMistakes(ops);
      if (!mistakes.length) {
        rows.push([p.pageName, `Field ${f.index + 1}`, f.accuracy, f.original || "", f.typed || "", "", "None", "", "", "", "No mistakes"]);
        continue;
      }
      mistakes.forEach((m, i) => {
        rows.push([
          i === 0 ? p.pageName : "",
          i === 0 ? `Field ${f.index + 1}` : "",
          i === 0 ? f.accuracy : "",
          i === 0 ? (f.original || "") : "",
          i === 0 ? (f.typed || "") : "",
          i + 1,
          CATEGORY_LABEL[m.category] || m.category,
          m.position,
          m.expected === null ? "" : m.expected === " " ? "[SPACE]" : m.expected,
          m.typed === null ? "" : m.typed === " " ? "[SPACE]" : m.typed,
          explain(m),
        ]);
      });
    }
  }
  const nm = `${(employee?.first_name || "Candidate")}_${employee?.last_name || ""}`.trim().replace(/\s+/g, "_");
  saveCsv(rows, `DataSprint_FullReport_${nm}.csv`);
}

function saveCsv(rows, filename) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
